import { sanitizeModelOutput } from "../../content/chat-template-sanitizer.js";
import { ShadowClawDatabase } from "../../db/db.js";
import { createLogMessage } from "../../worker/utils/createLogMessage.js";
import { createToolActivityMessage } from "../../worker/utils/createToolActivityMessage.js";
import { executeTool } from "../../worker/utils/executeTool.js";
import { setPostHandler } from "../../worker/utils/post.js";
import { DEFAULT_BUILTIN_PROFILE } from "../tools/builtin-profiles.js";
import { TOOL_DEFINITIONS, ToolDefinition } from "../tools/tools.js";
import {
  ensureBuiltinAiPolyfills,
  createTaskInstanceWithFallback,
  getPromptApiFallbackModel,
} from "./builtin-ai-tasks.js";
import {
  DEFAULT_PROMPT_API_MODEL_SIZE_BYTES,
  PromptApiProgressAggregator,
  setModelCacheProgressHook,
  clearModelCacheProgressHook,
  normalizeMessagesForChatTemplate,
} from "./utils/index.js";

import type { SubagentInvokeContext } from "../../worker/tools/spawn-subagent/spawn-subagent.js";

/**
 * Core tools exposed to the Prompt API on-device runtime.
 * Keeping this small prevents constrained local models from being
 * overwhelmed by too many tool definitions. All other tools remain
 * available to cloud-hosted providers.
 */
export { DEFAULT_BUILTIN_PROFILE };

const PROMPT_API_TOOLS = TOOL_DEFINITIONS.filter((t) =>
  DEFAULT_BUILTIN_PROFILE.enabledToolNames.includes(t.name),
);

export { PROMPT_API_TOOLS };

function getLanguageModelApi(): {
  availability: Function;
  create: Function;
} | null {
  const candidate = (globalThis as any).LanguageModel;
  if (!candidate || typeof candidate !== "function") {
    return null;
  }

  if (
    typeof candidate.availability !== "function" ||
    typeof candidate.create !== "function"
  ) {
    return null;
  }

  return candidate;
}

/**
 * Feature detection for the Prompt API.
 */
export function isPromptApiSupported(): boolean {
  return !!getLanguageModelApi();
}

function supportsPromptConstraintOptions(): boolean {
  // Conservative gate: if Prompt API exists, option bag support is still
  // experimental. We guard usage and gracefully retry without constraints.

  return true;
}

const PROMPT_IO_OPTIONS = {
  expectedInputs: [{ type: "text", languages: ["en"] }],
  expectedOutputs: [{ type: "text", languages: ["en"] }],
};

const logPromptApiEvents = false;
const PROMPT_API_CREATE_RETRY_MAX_ATTEMPTS = 4;
const PROMPT_API_CREATE_RETRY_DELAY_MS = 500;

// Keep the warm session window short to avoid prolonged CPU/GPU activity
// on constrained machines after a response is finished.
const WARM_SESSION_IDLE_MS = 10_000;

const warmSessionState: {
  key: string | null;
  session: any | null;
  cleanupTimer: ReturnType<typeof setTimeout> | null;
} = {
  key: null,
  session: null,
  cleanupTimer: null,
};

function clearWarmSessionTimer(): void {
  if (warmSessionState.cleanupTimer) {
    clearTimeout(warmSessionState.cleanupTimer);
    warmSessionState.cleanupTimer = null;
  }
}

async function destroyWarmSession(): Promise<void> {
  clearWarmSessionTimer();

  if (!warmSessionState.session) {
    warmSessionState.key = null;

    return;
  }

  await Promise.resolve(warmSessionState.session.destroy?.()).catch(() => {});

  warmSessionState.session = null;
  warmSessionState.key = null;
}

function scheduleWarmSessionCleanup(): void {
  clearWarmSessionTimer();
  warmSessionState.cleanupTimer = setTimeout(() => {
    void destroyWarmSession();
  }, WARM_SESSION_IDLE_MS);
}

function formatMessageContent(msg: any): string {
  if (typeof msg?.content === "string") {
    return msg.content;
  }

  if (Array.isArray(msg?.content)) {
    return msg.content
      .map((block: any) => {
        if (block?.type === "text") {
          return block.text || "";
        }
        if (block?.type === "tool_use") {
          return `[TOOL_CALL ${block.name}] ${JSON.stringify(block.input || {})}`;
        }
        if (block?.type === "tool_result") {
          return `[TOOL_RESULT ${block.tool_use_id}] ${String(block.content || "")}`;
        }
        if (block?.type === "attachment") {
          return `[ATTACHMENT ${block.mediaType}] ${block.fileName} (${block.mimeType})`;
        }
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }

  return "";
}

function buildInitialPrompts(
  systemPrompt: string,
  historyMessages: any[] = [],
): any[] {
  return normalizeMessagesForChatTemplate(historyMessages, systemPrompt);
}

async function emitModelDownloadProgress(
  emit: (message: any) => Promise<void> | void,
  groupId: string,
  status: "running" | "done" | "error",
  progress: number | null,
  message?: string,
): Promise<void> {
  await emit({
    type: "model-download-progress",
    payload: { groupId, status, progress, ...(message ? { message } : {}) },
  });
}

async function emitPromptApiDiagnostic(
  emit: (message: any) => Promise<void> | void,
  groupId: string,
  message: string,
): Promise<void> {
  await emit(createLogMessage(groupId, "info", "Prompt API", message));
}

function isPromptApiCreateNotReadyError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || "");
  const normalized = message.toLowerCase();

  return (
    normalized.includes("unable to create a session") &&
    (normalized.includes("availability()") ||
      normalized.includes("availability") ||
      normalized.includes("check the result"))
  );
}

async function waitForPromptApiCreateRetry(
  abortSignal: AbortSignal | undefined,
): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, PROMPT_API_CREATE_RETRY_DELAY_MS);
  });

  if (abortSignal?.aborted) {
    throw new Error("Prompt API session creation aborted.");
  }
}

function summarizePromptApiSession(
  session: any,
  languageModelApi: any,
): string {
  const model =
    detectPromptApiModelLabel(session, languageModelApi) || "unknown";
  const summary: Record<string, any> = { model };
  ["model", "modelId", "modelName", "name", "id"].forEach((key) => {
    const value = (session as any)[key];
    if (value !== undefined) {
      summary[key] = value;
    }
  });

  const sessionFields = Object.entries(summary)
    .map(([key, val]) => `${key}=${val}`)
    .join(", ");
  const capabilities = (session as any).capabilities || {};
  const metadata = (session as any).metadata || {};
  const capabilityKeys = Object.keys({ ...capabilities, ...metadata });

  return [
    `resolved model: ${model}`,
    sessionFields ? `session fields: ${sessionFields}` : "session fields: none",
    capabilityKeys.length > 0
      ? `capabilities: ${capabilityKeys.join(", ")}`
      : "capabilities: none",
  ].join(" | ");
}

export async function fetchPolyfillTotalSizeBytes(
  modelName: string,
  dtype: string,
): Promise<number> {
  // Step 1: Try Hugging Face tree API for an exact file size manifest
  try {
    const treeRes = await fetch(
      `https://huggingface.co/api/models/${encodeURI(modelName)}/tree/main?recursive=true`,
    );
    if (treeRes.ok) {
      const items = await treeRes.json();
      if (Array.isArray(items) && items.length > 0) {
        const files = items.filter((i) => i.type === "file");
        const metadataFiles = files.filter((f) =>
          [
            "config.json",
            "generation_config.json",
            "tokenizer.json",
            "tokenizer_config.json",
            "special_tokens_map.json",
            "added_tokens.json",
            "chat_template.jinja",
            "tokenizer.model",
          ].includes(f.path),
        );
        const metadataSize = metadataFiles.reduce(
          (acc, f) => acc + (Number(f.size) || 0),
          0,
        );

        let onnxFiles = files.filter((f) => {
          const p = String(f.path || "").toLowerCase();
          if (
            !p.endsWith(".onnx") &&
            !p.includes(".onnx_data") &&
            !p.includes(".onnx.data") &&
            !p.includes("_data")
          ) {
            return false;
          }
          if (dtype) {
            const cleanDtype = dtype.toLowerCase();
            if (
              p.includes(`model_${cleanDtype}.`) ||
              p.includes(`model_${cleanDtype}_`) ||
              p.includes(`_${cleanDtype}.`) ||
              p.includes(`_${cleanDtype}_`) ||
              p.includes(`/${cleanDtype}/`)
            ) {
              return true;
            }
          }
          return false;
        });

        if (onnxFiles.length === 0) {
          onnxFiles = files.filter((f) => {
            const p = String(f.path || "").toLowerCase();
            return (
              (p.includes("onnx/model.") ||
                p.includes("onnx/model_quantized.") ||
                p.includes("model.")) &&
              (p.endsWith(".onnx") ||
                p.includes(".onnx_data") ||
                p.includes(".onnx.data"))
            );
          });
        }

        const onnxSize = onnxFiles.reduce(
          (acc, f) => acc + (Number(f.size) || 0),
          0,
        );
        const treeTotal = metadataSize + onnxSize;
        if (treeTotal > 5 * 1024 * 1024) {
          return treeTotal;
        }
      }
    }
  } catch (err) {
    // Ignore tree API failure and proceed to HEAD fallback
  }

  // Step 2: Fallback to HEAD requests for candidate files
  const candidateFiles = [
    "config.json",
    "generation_config.json",
    "tokenizer.json",
    "tokenizer_config.json",
    "special_tokens_map.json",
    `onnx/model_${dtype}.onnx`,
    `onnx/model_${dtype}.onnx_data`,
    `onnx/model_${dtype}.onnx.data`,
    `onnx/model.onnx`,
    `onnx/model.onnx_data`,
    `onnx/model_quantized.onnx`,
    `onnx/model_quantized.onnx_data`,
  ];

  try {
    const sizes = await Promise.all(
      candidateFiles.map(async (file) => {
        try {
          const res = await fetch(
            `https://huggingface.co/${modelName}/resolve/main/${file}`,
            { method: "HEAD" },
          );
          if (res.ok) {
            return parseInt(res.headers.get("content-length") || "0", 10);
          }
        } catch {}
        return 0;
      }),
    );

    const headTotal = sizes.reduce((a, b) => a + b, 0);
    if (headTotal > 5 * 1024 * 1024) {
      return headTotal;
    }
  } catch {}

  return DEFAULT_PROMPT_API_MODEL_SIZE_BYTES;
}

export type PromptApiSamplingMode =
  | "most-predictable"
  | "predictable"
  | "slightly-predictable"
  | "balanced"
  | "slightly-creative"
  | "creative"
  | "most-creative";

export interface PromptApiSamplingOptions {
  samplingMode?: PromptApiSamplingMode;
  temperature?: number;
  topK?: number;
}

async function createPromptSessionWithProgress(
  emit: (message: any) => Promise<void> | void,
  groupId: string,
  abortSignal: AbortSignal | undefined,
  initialPrompts: any[] = [],
  emitErrorOnFailure = true,
  samplingOptions?: PromptApiSamplingOptions,
  tools?: ToolDefinition[],
) {
  await ensureBuiltinAiPolyfills();
  const LanguageModelApi = getLanguageModelApi();
  if (!LanguageModelApi) {
    throw new Error(
      "Prompt API is unavailable in this browser. Enable Prompt API flags in a supported browser (for example Edge Dev/Canary or Chrome with the required flags) or switch provider.",
    );
  }

  const samplingParams = {
    ...(samplingOptions?.samplingMode
      ? { samplingMode: samplingOptions.samplingMode }
      : {}),
    ...(typeof samplingOptions?.temperature === "number" &&
    Number.isFinite(samplingOptions.temperature)
      ? { temperature: samplingOptions.temperature }
      : {}),
    ...(typeof samplingOptions?.topK === "number" &&
    Number.isFinite(samplingOptions.topK)
      ? { topK: Math.floor(samplingOptions.topK) }
      : {}),
  };

  const nativeTools =
    tools && tools.length > 0
      ? tools.map((t) => ({
          name: t.name,
          description: t.description,
          inputSchema: t.input_schema,
          parameters: t.input_schema,
          ...(typeof (t as any).execute === "function"
            ? { execute: (t as any).execute }
            : {}),
        }))
      : undefined;

  const availability = await LanguageModelApi.availability({
    ...PROMPT_IO_OPTIONS,
    ...samplingParams,
    ...(nativeTools ? { tools: nativeTools } : {}),
    ...(initialPrompts && initialPrompts.length > 0 ? { initialPrompts } : {}),
  });
  await emitPromptApiDiagnostic(
    emit,
    groupId,
    `availability() returned: ${availability}`,
  );

  if (availability === "unavailable" || availability === "no") {
    throw new Error(
      "Prompt API model is unavailable on this device/browser. Switch provider or enable the feature flags.",
    );
  }

  const isReadilyAvailable = availability === "readily";
  const needsDownload =
    availability !== "readily" && availability !== "available";

  const isPolyfill =
    !!(globalThis as any).TRANSFORMERS_CONFIG ||
    (availability === "available" &&
      !("LanguageModel" in globalThis) &&
      !(globalThis as any).ai?.languageModel);

  let expectedTotalBytes = 0;
  if (isPolyfill) {
    const config = (globalThis as any).TRANSFORMERS_CONFIG || {};
    const modelName = config.modelName || (await getPromptApiFallbackModel());
    const dtype = config.dtype || "q4";
    expectedTotalBytes = await fetchPolyfillTotalSizeBytes(modelName, dtype);
  }

  const aggregator = new PromptApiProgressAggregator(
    (status, progress, message) => {
      void emitModelDownloadProgress(emit, groupId, status, progress, message);
    },
    {
      expectedTotalBytes,
      suppressInitialZero: true,
      logEvents: logPromptApiEvents,
    },
  );

  if (needsDownload) {
    aggregator.markActive();
    await emitModelDownloadProgress(
      emit,
      groupId,
      "running",
      availability === "downloading" ? null : 0,
      "Downloading Prompt API model...",
    );
  }

  let session;
  let latestAvailability = availability;
  try {
    setModelCacheProgressHook((url, received, total, complete) => {
      aggregator.onStreamProgress(url, received, total, !!complete);
    });

    const monitorOption = !isReadilyAvailable
      ? {
          monitor(monitorTarget: any) {
            if (logPromptApiEvents) {
              console.log(
                "[PromptAPI] monitor() called, monitorTarget:",
                monitorTarget,
              );
            }

            if (
              !monitorTarget ||
              typeof monitorTarget.addEventListener !== "function"
            ) {
              if (logPromptApiEvents) {
                console.warn(
                  "[PromptAPI] monitorTarget is invalid or missing addEventListener",
                );
              }

              return;
            }

            if (logPromptApiEvents) {
              console.log(
                "[PromptAPI] Registering downloadprogress listener on monitorTarget",
              );
            }

            monitorTarget.addEventListener("downloadprogress", (event: any) => {
              aggregator.onProgressEvent(event);
            });
          },
        }
      : {};

    for (
      let attempt = 1;
      attempt <= PROMPT_API_CREATE_RETRY_MAX_ATTEMPTS;
      attempt++
    ) {
      await emitPromptApiDiagnostic(
        emit,
        groupId,
        `Creating Prompt API session... (attempt ${attempt}/${PROMPT_API_CREATE_RETRY_MAX_ATTEMPTS})`,
      );

      try {
        session = await createTaskInstanceWithFallback(
          LanguageModelApi as any,
          {
            ...PROMPT_IO_OPTIONS,
            ...samplingParams,
            ...(nativeTools ? { tools: nativeTools } : {}),
            ...(initialPrompts.length > 0 ? { initialPrompts } : {}),
            signal: abortSignal,
            ...monitorOption,
          },
        );

        break;
      } catch (createErr) {
        const stillDownloading =
          latestAvailability !== "readily" &&
          latestAvailability !== "available" &&
          latestAvailability !== "unavailable" &&
          latestAvailability !== "no";
        const canRetryForDownloading =
          stillDownloading && isPromptApiCreateNotReadyError(createErr);

        if (
          !canRetryForDownloading ||
          attempt >= PROMPT_API_CREATE_RETRY_MAX_ATTEMPTS
        ) {
          throw createErr;
        }

        await emitPromptApiDiagnostic(
          emit,
          groupId,
          "Session creation failed while model is still downloading; retrying shortly...",
        );

        latestAvailability = await LanguageModelApi.availability({
          ...PROMPT_IO_OPTIONS,
          ...samplingParams,
          ...(nativeTools ? { tools: nativeTools } : {}),
          ...(initialPrompts && initialPrompts.length > 0
            ? { initialPrompts }
            : {}),
        });
        await emitPromptApiDiagnostic(
          emit,
          groupId,
          `availability() recheck returned: ${latestAvailability}`,
        );

        if (
          latestAvailability !== "readily" &&
          latestAvailability !== "available" &&
          latestAvailability !== "unavailable" &&
          latestAvailability !== "no"
        ) {
          if (!aggregator.hasEmitted) {
            await emitModelDownloadProgress(
              emit,
              groupId,
              "running",
              latestAvailability === "downloading" ? null : 0,
              "Downloading Prompt API model...",
            );
          }
        }

        await waitForPromptApiCreateRetry(abortSignal);
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (emitErrorOnFailure) {
      aggregator.onError(err);
    }

    await emitPromptApiDiagnostic(
      emit,
      groupId,
      `Session creation failed: ${message}`,
    );

    throw err;
  } finally {
    clearModelCacheProgressHook();
  }

  await emitPromptApiDiagnostic(
    emit,
    groupId,
    summarizePromptApiSession(session, LanguageModelApi),
  );

  if (needsDownload || aggregator.hasEmitted) {
    aggregator.onDone();
  }

  return session;
}

export function isPromptApiMonitorFallbackError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || "");
  const normalized = message.toLowerCase();

  return (
    normalized.includes("monitor option") ||
    normalized.includes("downloadprogresscallback") ||
    normalized.includes("dictionary") ||
    normalized.includes("initialprompts") ||
    (normalized.includes("not supported") &&
      (normalized.includes("option") || normalized.includes("monitor")))
  );
}

function mapPromptApiModelLabel(raw: string): string {
  const normalized = raw.toLowerCase();
  if (
    normalized.includes("gemma4") ||
    normalized.includes("gemma 4") ||
    normalized.includes("gemma-4")
  ) {
    return "Gemma 4";
  }

  if (
    normalized.includes("nano_v3") ||
    normalized.includes("gemini nano") ||
    normalized.includes("gemini-nano") ||
    normalized.includes("prompt_api")
  ) {
    return "Gemini Nano";
  }

  return raw;
}

function detectPromptApiModelLabel(
  session: any,
  languageModelApi: any,
): string | null {
  const labels = [
    (session as any).model,
    (session as any).modelId,
    (session as any).modelName,
    (session as any).id,
    (session as any).name,
    (languageModelApi as any).model,
    (languageModelApi as any).modelId,
    (languageModelApi as any).modelName,
    (languageModelApi as any).id,
    (languageModelApi as any).name,
  ];
  const label = labels.find((l) => typeof l === "string" && l.trim());

  return label ? mapPromptApiModelLabel(label.trim()) : "Gemini Nano";
}

/*
function summarizePromptApiCapabilities(
  session: any,
  languageModelApi: any,
): Record<string, unknown> {
  const candidates = [
    (session as any).capabilities,
    (session as any).metadata,
    (session as any).info,
    (languageModelApi as any).capabilities,
  ];

  for (const obj of candidates) {
    if (obj && typeof obj === "object") {
      return obj as Record<string, unknown>;
    }
  }

  return {};
}

*/

async function createPromptSessionDirect(
  emit: (message: any) => Promise<void> | void,
  groupId: string,
  systemPrompt: string,
  abortSignal: AbortSignal | undefined,
  samplingOptions?: PromptApiSamplingOptions,
  tools?: ToolDefinition[],
  historyMessages: any[] = [],
) {
  await ensureBuiltinAiPolyfills();
  const LanguageModelApi = getLanguageModelApi();
  if (!LanguageModelApi) {
    throw new Error(
      "Prompt API is unavailable in this browser. Enable Prompt API flags in a supported browser (for example Edge Dev/Canary or Chrome with the required flags) or switch provider.",
    );
  }

  const initialPrompts = buildInitialPrompts(systemPrompt, historyMessages);
  const samplingParams = {
    ...(samplingOptions?.samplingMode
      ? { samplingMode: samplingOptions.samplingMode }
      : {}),
    ...(typeof samplingOptions?.temperature === "number" &&
    Number.isFinite(samplingOptions.temperature)
      ? { temperature: samplingOptions.temperature }
      : {}),
    ...(typeof samplingOptions?.topK === "number" &&
    Number.isFinite(samplingOptions.topK)
      ? { topK: Math.floor(samplingOptions.topK) }
      : {}),
  };

  const nativeTools =
    tools && tools.length > 0
      ? tools.map((t) => ({
          name: t.name,
          description: t.description,
          inputSchema: t.input_schema,
          parameters: t.input_schema,
        }))
      : undefined;

  return await createPromptSessionWithProgress(
    emit,
    groupId,
    abortSignal,
    initialPrompts,
    false,
    samplingOptions,
    tools,
  ).catch(async (error) => {
    // Fallback to plain creation if monitor-path options fail on this build.
    if (!isPromptApiMonitorFallbackError(error)) {
      throw error;
    }

    let fallbackSession;
    try {
      fallbackSession = await createTaskInstanceWithFallback(
        LanguageModelApi as any,
        {
          ...PROMPT_IO_OPTIONS,
          ...samplingParams,
          ...(nativeTools ? { tools: nativeTools } : {}),
          ...(initialPrompts.length > 0 ? { initialPrompts } : {}),
          signal: abortSignal,
        },
      );
    } catch (fallbackError) {
      const message =
        fallbackError instanceof Error
          ? fallbackError.message
          : String(fallbackError);
      await emitModelDownloadProgress(emit, groupId, "error", null, message);

      throw fallbackError;
    }

    const availability = await LanguageModelApi.availability({
      ...PROMPT_IO_OPTIONS,
      ...samplingParams,
      ...(nativeTools ? { tools: nativeTools } : {}),
      ...(initialPrompts && initialPrompts.length > 0
        ? { initialPrompts }
        : {}),
    }).catch(() => "available");
    const needsDownload =
      availability !== "readily" && availability !== "available";

    if (needsDownload) {
      await emitModelDownloadProgress(
        emit,
        groupId,
        "done",
        1,
        "Prompt API model ready.",
      );
    }

    return fallbackSession;
  });
}

async function acquirePromptSession(
  emit: (message: any) => Promise<void> | void,
  groupId: string,
  systemPrompt: string,
  abortSignal: AbortSignal | undefined,
  samplingOptions?: PromptApiSamplingOptions,
  tools?: ToolDefinition[],
  historyMessages: any[] = [],
) {
  const promptKey =
    String(systemPrompt || "") +
    (historyMessages.length ? JSON.stringify(historyMessages) : "");

  clearWarmSessionTimer();

  if (
    warmSessionState.session &&
    warmSessionState.key === promptKey &&
    typeof warmSessionState.session.clone === "function"
  ) {
    try {
      const clone = await warmSessionState.session.clone();
      scheduleWarmSessionCleanup();

      return { session: clone, destroyAfterUse: true };
    } catch {
      await destroyWarmSession();
    }
  }

  await destroyWarmSession();

  const session = await createPromptSessionDirect(
    emit,
    groupId,
    systemPrompt,
    abortSignal,
    samplingOptions,
    tools,
    historyMessages,
  );

  if (typeof session.clone === "function") {
    warmSessionState.session = session;
    warmSessionState.key = promptKey;
    scheduleWarmSessionCleanup();
    try {
      const clone = await session.clone();

      return { session: clone, destroyAfterUse: true };
    } catch {
      // If cloning fails, use the original session directly
    }
  }

  return { session, destroyAfterUse: true };
}

function buildPromptTranscript(
  systemPrompt: string,
  messages: any[],
  toolResultHints: string[] = [],
  includeSystemPrompt = true,
): string {
  const lines = includeSystemPrompt
    ? ["SYSTEM INSTRUCTIONS:", systemPrompt, "", "CONVERSATION:"]
    : ["CONVERSATION:"];

  for (const msg of messages) {
    const role = msg?.role === "assistant" ? "ASSISTANT" : "USER";

    if (typeof msg?.content === "string") {
      lines.push(`${role}: ${msg.content}`);

      continue;
    }

    if (Array.isArray(msg?.content)) {
      const blocks = msg.content
        .map((block: any) => {
          if (block?.type === "text") {
            return block.text || "";
          }

          if (block?.type === "tool_use") {
            return `[TOOL_CALL ${block.name}] ${JSON.stringify(block.input || {})}`;
          }

          if (block?.type === "tool_result") {
            return `[TOOL_RESULT ${block.tool_use_id}] ${String(block.content || "")}`;
          }

          if (block?.type === "attachment") {
            return `[ATTACHMENT ${block.mediaType}] ${block.fileName} (${block.mimeType})`;
          }

          return "";
        })
        .filter(Boolean)
        .join("\n");

      lines.push(`${role}: ${blocks}`);

      continue;
    }

    lines.push(`${role}:`);
  }

  if (toolResultHints.length > 0) {
    lines.push("", "TOOL RESULT HINTS:");
    for (const hint of toolResultHints) {
      lines.push(`- ${hint}`);
    }
  }

  return lines.join("\n");
}

function parseStructured(raw: string): {
  type: "response" | "tool_use";
  response?: string;
  tool_calls?: Array<{
    id?: string;
    name: string;
    input?: Record<string, any>;
  }>;
} | null {
  const text = sanitizeModelOutput(String(raw || ""), "prompt_api").trim();
  if (!text) {
    return null;
  }

  // Check for Qwen/Llama XML-like tool calls: <tool_call>{"name": "...", "arguments": ...}</tool_call>
  const toolCallMatches = [
    ...text.matchAll(/<tool_call>([\s\S]*?)<\/tool_call>/g),
  ];
  if (toolCallMatches.length > 0) {
    const toolCalls: Array<{
      id?: string;
      name: string;
      input: Record<string, any>;
    }> = [];
    for (const match of toolCallMatches) {
      try {
        const parsedTool = JSON.parse(match[1].trim());
        const toolName = parsedTool.name || parsedTool.function?.name;
        if (toolName) {
          toolCalls.push({
            id: parsedTool.id || `call_${Date.now()}_${toolCalls.length}`,
            name: toolName,
            input:
              parsedTool.arguments ||
              parsedTool.function?.arguments ||
              parsedTool.parameters ||
              parsedTool.input ||
              {},
          });
        }
      } catch {
        // fallback
      }
    }
    if (toolCalls.length > 0) {
      return {
        type: "tool_use",
        tool_calls: toolCalls,
      };
    }
  }

  let obj: any = null;
  try {
    obj = JSON.parse(text);
  } catch {
    const start = text.indexOf("{");
    const end = text.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        obj = JSON.parse(text.slice(start, end + 1));
      } catch {
        obj = null;
      }
    }
  }

  if (obj && typeof obj === "object") {
    if (obj.type === "tool_use" || Array.isArray(obj.tool_calls)) {
      return {
        type: "tool_use",
        tool_calls: Array.isArray(obj.tool_calls) ? obj.tool_calls : [],
        response: typeof obj.response === "string" ? obj.response : undefined,
      };
    }

    const extractedText =
      typeof obj.response === "string"
        ? obj.response
        : typeof obj.text === "string"
          ? obj.text
          : typeof obj.message === "string"
            ? obj.message
            : typeof obj.content === "string"
              ? obj.content
              : typeof obj.answer === "string"
                ? obj.answer
                : undefined;

    if (extractedText !== undefined || obj.type === "response") {
      return {
        type: "response",
        response: extractedText,
      };
    }
  }

  return null;
}

export async function invokeWithPromptApi(
  db: ShadowClawDatabase,
  groupId: string,
  systemPrompt: string,
  messages: any[],
  _maxTokens: number,
  emit: (message: any) => Promise<void> | void,
  abortSignal: AbortSignal | undefined,
  tools: ToolDefinition[] | undefined,
  invokeContext?: SubagentInvokeContext,
  samplingOptions?: PromptApiSamplingOptions,
) {
  const activeTools = tools || PROMPT_API_TOOLS;

  const LanguageModelApi = getLanguageModelApi();

  await emit(
    createLogMessage(
      groupId,
      "info",
      "Starting",
      `Provider: Prompt API (initializing) · ${activeTools.length} tools`,
    ),
  );

  const historyMessages = messages.length > 1 ? messages.slice(0, -1) : [];
  const latestMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;

  const fullSystemPrompt = systemPrompt;

  const { session, destroyAfterUse } = await acquirePromptSession(
    emit,
    groupId,
    fullSystemPrompt,
    abortSignal,
    samplingOptions,
    activeTools,
    historyMessages,
  );

  const runtimeModel = detectPromptApiModelLabel(session, LanguageModelApi);
  const runtimeLabel = runtimeModel || "On-device";

  await emit(
    createLogMessage(
      groupId,
      "info",
      "Provider",
      `Provider: Prompt API (${runtimeLabel}) · ${activeTools.length} tools`,
    ),
  );

  if (!runtimeModel) {
    await emit(
      createLogMessage(
        groupId,
        "info",
        "Model",
        "Prompt API runtime did not expose an exact model id.",
      ),
    );
  }

  try {
    const toolJsonSchema = {
      type: "object",
      additionalProperties: false,
      properties: {
        type: {
          type: "string",
          enum: ["response", "tool_use"],
        },
        response: {
          type: "string",
        },
        tool_calls: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            properties: {
              id: { type: "string" },
              name: {
                type: "string",
                enum: activeTools.map((t) => t.name),
              },
              input: { type: "object" },
            },
            required: ["name", "input"],
          },
        },
      },
      required: ["type"],
    };

    let currentMessages = [...messages];
    for (let i = 0; i < 12; i++) {
      await emit(
        createLogMessage(
          groupId,
          "api-call",
          `Prompt API call #${i + 1}`,
          `${currentMessages.length} messages in context`,
        ),
      );

      // First iteration: send the latest user message.
      // Subsequent iterations: the session already has prior context, so only
      // send the latest tool results.
      let prompt;
      if (i === 0) {
        prompt = formatMessageContent(latestMessage) || "Continue.";
      } else {
        const lastEntry = currentMessages[currentMessages.length - 1];
        const resultLines = Array.isArray(lastEntry?.content)
          ? lastEntry.content.map(
              (r: any) =>
                `[TOOL_RESULT ${r.tool_use_id}] ${typeof r.content === "string" ? r.content : JSON.stringify(r.content)}`,
            )
          : [String(lastEntry?.content || "")];

        // Re-state the user's original request so Nano remembers the full goal.
        const lastUserText =
          [...messages]
            .reverse()
            .find((m) => m?.role === "user" && typeof m.content === "string")
            ?.content || "";

        prompt = [
          "Tool execution results:",
          ...resultLines,
          "",
          lastUserText ? `Original user request: "${lastUserText}"` : "",
          "If the user's original request is NOW fully satisfied, provide a final response.",
          "Otherwise, call the next required tool.",
        ]
          .filter(Boolean)
          .join("\n");
      }

      let raw: string;

      /**
       * Stream the response from promptStreaming(), emitting typing/progress
       * events so the UI shows "Responding" and the activity log updates.
       */
      async function consumeStream(
        streamOrPromise: AsyncIterable<string>,
        emit: (message: any) => Promise<void> | void,
        groupId: string,
      ) {
        // Signal the UI that a streaming response is starting
        await emit({
          type: "streaming-start",
          payload: { groupId },
        });

        let accumulated = "";
        let lastMeaningfulLength = 0;
        let staleCount = 0;
        const MAX_STALE = 3;
        let chunkCount = 0;
        let lastExtractedLength = 0;

        for await (const chunk of streamOrPromise) {
          const text = String(chunk);
          chunkCount++;

          // Heuristic: if the chunk is longer than accumulated so far, the API
          // is returning cumulative text (each chunk = full response to date).
          // Otherwise treat it as a delta and append.
          if (
            text.length >= accumulated.length &&
            text.startsWith(accumulated.slice(0, 20))
          ) {
            accumulated = text; // cumulative
          } else {
            accumulated += text; // delta
          }

          // Extract text from JSON if possible for the UI streaming bubble.
          // We do this at the top of the loop to ensure even the final chunk
          // is processed before the "complete JSON" break.
          let textToStream = "";
          const rawStr = accumulated.trimStart();
          if (
            rawStr.startsWith("<tool_call>") ||
            rawStr.startsWith('{"type":"tool_use"')
          ) {
            // It's a tool call, don't stream to chat
            textToStream = "";
          } else if (rawStr.startsWith("{")) {
            // Heuristic for {"type":"response","response":"..."}
            const match = rawStr.match(/"response"\s*:\s*"((?:[^"\\]|\\.)*)/);
            if (match) {
              textToStream = match[1]
                .replace(/\\"/g, '"')
                .replace(/\\n/g, "\n");
            }
          } else {
            // Not JSON, stream raw text
            textToStream = sanitizeModelOutput(rawStr, "prompt_api");
          }

          if (textToStream && textToStream.length > lastExtractedLength) {
            const delta = textToStream.slice(lastExtractedLength);
            lastExtractedLength = textToStream.length;

            await emit({
              type: "streaming-chunk",
              payload: { groupId, text: delta },
            });
          }

          const trimmed = accumulated.trim();
          const trimmedLen = trimmed.length;

          // Detect stale: no new meaningful (non-whitespace) content
          if (trimmedLen <= lastMeaningfulLength) {
            staleCount++;
            if (staleCount >= MAX_STALE) {
              break;
            }

            continue;
          }

          staleCount = 0;
          lastMeaningfulLength = trimmedLen;

          // Detect complete JSON — the stream may never close, so once we have
          // a parseable JSON object that matches our expected envelope, stop.
          if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            try {
              const test = JSON.parse(trimmed);
              if (
                test &&
                (test.type === "response" || test.type === "tool_use")
              ) {
                break;
              }
            } catch {
              // Not yet valid JSON — keep streaming
            }
          }

          // Emit typing so UI transitions from "Thinking" → "Responding"
          if (chunkCount === 1) {
            await emit({ type: "typing", payload: { groupId } });
          }

          // Log progress at milestones only — first chunk, then every ~500 chars
          const charMilestone = Math.floor(accumulated.length / 500);
          const lastMilestone = Math.floor(
            (accumulated.length - (trimmedLen - lastMeaningfulLength)) / 500,
          );
          if (chunkCount === 1 || charMilestone > lastMilestone) {
            const tail =
              accumulated.length > 100
                ? "…" + accumulated.slice(-100)
                : accumulated;
            await emit(
              createLogMessage(
                groupId,
                "streaming",
                `Generating (${accumulated.length} chars)`,
                tail,
              ),
            );
          }
        }

        return accumulated;
      }

      // Defensive: catch 'session has been destroyed' and log session object in Edge
      async function tryPromptStreamingWithSession(
        sessionObj: any,
        prompt: string,
        options: any,
      ) {
        try {
          return await consumeStream(
            sessionObj.promptStreaming(prompt, options),
            emit,
            groupId,
          );
        } catch (err: any) {
          if (
            err instanceof Error &&
            err.message &&
            err.message.toLowerCase().includes("session has been destroyed")
          ) {
            // Log the session object to DevTools for debugging
            // eslint-disable-next-line no-console
            console.warn(
              "[PromptAPI] Session was destroyed. Session object:",
              sessionObj,
            );
            // Optionally, log more context
            // eslint-disable-next-line no-console
            console.warn(
              "[PromptAPI] Session fields:",
              Object.keys(sessionObj || {}),
            );
          }

          throw err;
        }
      }

      if (activeTools.length > 0 && supportsPromptConstraintOptions()) {
        try {
          raw = await tryPromptStreamingWithSession(session, prompt, {
            signal: abortSignal,
            responseConstraint: toolJsonSchema,
            omitResponseConstraintInput: true,
          });
          // If the constrained stream stalled on incomplete JSON (e.g. '{"'),
          // retry without the constraint so the model can freely generate.
          if (!parseStructured(raw)) {
            raw = await tryPromptStreamingWithSession(session, prompt, {
              signal: abortSignal,
            });
          }
        } catch {
          raw = await tryPromptStreamingWithSession(session, prompt, {
            signal: abortSignal,
          });
        }
      } else {
        raw = await tryPromptStreamingWithSession(session, prompt, {
          signal: abortSignal,
        });
      }

      const parsed = parseStructured(raw);

      // Signal end of stream before processing results
      if (parsed?.type === "tool_use") {
        await emit({
          type: "streaming-end",
          payload: { groupId },
        });
      } else {
        // Extract final text for streaming-done
        let finalText = parsed?.response
          ? sanitizeModelOutput(parsed.response, "prompt_api")
          : undefined;
        if (finalText === undefined) {
          finalText = sanitizeModelOutput(
            String(raw || "").trim(),
            "prompt_api",
          );
        }

        await emit({
          type: "streaming-done",
          payload: { groupId, text: finalText || "(no response)" },
        });
      }

      if (!parsed || parsed.type === "response") {
        let text = parsed?.response
          ? sanitizeModelOutput(parsed.response, "prompt_api")
          : undefined;
        if (text === undefined) {
          text = sanitizeModelOutput(String(raw || "").trim(), "prompt_api");
        }

        const preview =
          (text || "").length > 200
            ? (text || "").slice(0, 200) + "…"
            : text || "(no response)";
        await emit(createLogMessage(groupId, "text", "Response text", preview));
        await emit({
          type: "response",
          payload: { groupId, text: text || "(no response)" },
        });

        return;
      }

      const calls = Array.isArray(parsed.tool_calls) ? parsed.tool_calls : [];
      if (calls.length === 0) {
        await emit({
          type: "response",
          payload: {
            groupId,
            text: parsed.response || "(no response)",
          },
        });

        return;
      }

      const toolResults: any[] = [];
      // Defer open-file messages until all tool calls in the batch complete.
      // Nano may emit open_file before write_file in the same batch — deferring
      // ensures the file exists on disk before the UI tries to read it.
      const deferredOpenFileMessages: any[] = [];

      for (const call of calls) {
        const inputPreview = JSON.stringify(call.input)?.slice(0, 200);
        await emit(
          createLogMessage(
            groupId,
            "tool-call",
            `Tool: ${call.name}`,
            inputPreview,
          ),
        );

        await emit(createToolActivityMessage(groupId, call.name, "running"));

        let toolOutput;
        // Route worker-originated side effects (toast/open-file/task updates)
        // to the orchestrator's normal inbound handler, but hold open-file.
        setPostHandler((msg) => {
          if (msg?.type === "open-file") {
            deferredOpenFileMessages.push(msg);
          } else {
            emit(msg);
          }
        });
        try {
          toolOutput = await executeTool(
            db,
            call.name,
            call.input || {},
            groupId,
            { allowedTools: activeTools, invokeContext },
          );
        } finally {
          setPostHandler(null);
        }

        const outputPreview =
          typeof toolOutput === "string"
            ? toolOutput.slice(0, 200)
            : JSON.stringify(toolOutput)?.slice(0, 200);
        await emit(
          createLogMessage(
            groupId,
            "tool-result",
            `Result: ${call.name}`,
            outputPreview,
          ),
        );

        await emit(createToolActivityMessage(groupId, call.name, "done"));

        toolResults.push({
          type: "tool_result",
          tool_use_id: call.id || `${call.name}-${Date.now()}`,
          content:
            typeof toolOutput === "string"
              ? toolOutput
              : JSON.stringify(toolOutput),
        });
      }

      // Now that all tool calls are done, emit deferred open-file messages.
      for (const msg of deferredOpenFileMessages) {
        await emit(msg);
      }

      currentMessages.push({
        role: "assistant",
        content: calls.map((call) => ({
          type: "tool_use",
          id: call.id || `${call.name}-${Date.now()}`,
          name: call.name,
          input: call.input || {},
        })),
      });
      currentMessages.push({ role: "user", content: toolResults });
      await emit({ type: "typing", payload: { groupId } });
    }

    await emit({
      type: "response",
      payload: {
        groupId,
        text: "⚠️ Reached maximum Prompt API tool-use iterations (12).",
      },
    });
  } finally {
    if (destroyAfterUse) {
      await Promise.resolve(session.destroy?.()).catch(() => {});
    }

    scheduleWarmSessionCleanup();
  }
}

export async function compactWithPromptApi(
  systemPrompt: string,
  messages: any[],
  abortSignal: AbortSignal,
  emit: (message: any) => Promise<void> | void,
  groupId = "br:main",
) {
  const fallbackEmit = emit || (async () => {});
  const { session, destroyAfterUse } = await acquirePromptSession(
    fallbackEmit,
    groupId,
    systemPrompt,
    abortSignal,
  );
  try {
    const compactPrompt = [
      "Summarize the following chat history concisely.",
      "Keep key facts, decisions, preferences, unresolved tasks, and constraints.",
      "Return plain text only.",
      "",
      buildPromptTranscript(systemPrompt, messages, [], false),
    ].join("\n");

    const summary = await session.prompt(compactPrompt, {
      signal: abortSignal,
    });

    return String(summary || "").trim();
  } finally {
    if (destroyAfterUse) {
      await Promise.resolve(session.destroy?.()).catch(() => {});
    }

    scheduleWarmSessionCleanup();
  }
}

/**
 * Test-only hook: clear cached warm session state.
 */
export async function __resetPromptApiSessionCacheForTests(): Promise<void> {
  await destroyWarmSession();
}
